import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Tabs,
    Statistic,
    Space,
    Select,
    DatePicker,
    Spin,
    Alert,
    Typography,
    Badge,
    Collapse,
    theme,
} from 'antd';
import {
    DollarOutlined,
    RiseOutlined,
    FallOutlined,
    FileTextOutlined,
    BarChartOutlined,
    LineChartOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { financeApi } from '../../api/client';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import CostsActualTab from './components/CostsActualTab';
import CostsPlanTab from './components/CostsPlanTab';
import { CostsCharts } from './components/CostsCharts';

dayjs.extend(quarterOfYear);

const { RangePicker } = DatePicker;
const { Title } = Typography;

// Period presets
const PERIOD_PRESETS = [
    { value: 'last30', label: 'Ostatnie 30 dni' },
    { value: 'thisMonth', label: 'Ten miesiąc' },
    { value: 'thisQuarter', label: 'Ten kwartał' },
    { value: 'ytd', label: 'Od początku roku' },
    { value: 'custom', label: 'Zakres dat...' },
];

// Cost categories for display
export const COST_CATEGORIES = [
    { value: 'PROCUREMENT', label: 'Zakup i Produkcja' },
    { value: 'COGS', label: 'Koszt Towaru (Korekty)' },
    { value: 'MARKETING', label: 'Marketing i Reklama' },
    { value: 'OPERATIONS', label: 'Operacje' },
    { value: 'PERSONNEL', label: 'Kadry i Wynagrodzenia' },
    { value: 'TECHNOLOGY', label: 'Technologia i Narzędzia' },
    { value: 'LOGISTICS', label: 'Logistyka i Magazyn' },
    { value: 'LEGAL', label: 'Koszty Prawne i Księgowe' },
    { value: 'OTHER', label: 'Pozostałe' },
];

// Payment status
export const PAYMENT_STATUSES = [
    { value: 'PAID', label: 'Opłacone', color: 'green' },
    { value: 'UNPAID', label: 'Nieopłacone', color: 'orange' },
    { value: 'OVERDUE', label: 'Przeterminowane', color: 'red' },
];

export default function FinancePage() {
    const [period, setPeriod] = useState('thisMonth');
    const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [activeTab, setActiveTab] = useState('actual');
    const [searchParams] = useSearchParams();
    const [chartsExpanded, setChartsExpanded] = useState(() => {
        const saved = localStorage.getItem('finance-charts-expanded');
        return saved !== null ? saved === 'true' : false;
    });
    const [overdueFilter, setOverdueFilter] = useState(false);
    const { token } = theme.useToken();

    // Check for deep link
    useEffect(() => {
        if (searchParams.get('costId')) {
            setActiveTab('actual');
        }
    }, [searchParams]);

    // Calculate date range based on period
    const getDateRange = (): { startDate: string; endDate: string } => {
        const now = dayjs();
        switch (period) {
            case 'last30':
                return {
                    startDate: now.subtract(30, 'day').format('YYYY-MM-DD'),
                    endDate: now.format('YYYY-MM-DD'),
                };
            case 'thisMonth':
                return {
                    startDate: now.startOf('month').format('YYYY-MM-DD'),
                    endDate: now.endOf('month').format('YYYY-MM-DD'),
                };
            case 'thisQuarter':
                return {
                    startDate: now.startOf('quarter').format('YYYY-MM-DD'),
                    endDate: now.endOf('quarter').format('YYYY-MM-DD'),
                };
            case 'ytd':
                return {
                    startDate: now.startOf('year').format('YYYY-MM-DD'),
                    endDate: now.format('YYYY-MM-DD'),
                };
            case 'custom':
                if (customRange) {
                    return {
                        startDate: customRange[0].format('YYYY-MM-DD'),
                        endDate: customRange[1].format('YYYY-MM-DD'),
                    };
                }
                return {
                    startDate: now.startOf('month').format('YYYY-MM-DD'),
                    endDate: now.endOf('month').format('YYYY-MM-DD'),
                };
            default:
                return {
                    startDate: now.startOf('month').format('YYYY-MM-DD'),
                    endDate: now.endOf('month').format('YYYY-MM-DD'),
                };
        }
    };

    const { startDate, endDate } = getDateRange();
    const currentYear = new Date().getFullYear();

    // Fetch overview data
    const { data: overview, isLoading: overviewLoading, error: overviewError } = useQuery({
        queryKey: ['costsOverview', startDate, endDate, currentYear],
        queryFn: () => financeApi.getCostsOverview({ startDate, endDate, year: currentYear }).then(res => res.data),
    });

    if (overviewLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" tip="Ładowanie danych..." />
            </div>
        );
    }

    if (overviewError) {
        return <Alert type="error" message="Błąd ładowania danych" description={(overviewError as Error).message} />;
    }

    const variance = overview?.variance ?? 0;
    const variancePercent = overview?.variancePercent ?? 0;
    const isOverBudget = variance > 0;

    // Period label helper (same pattern as Orders)
    const getPeriodLabel = (preset: string) => {
        const now = dayjs();
        switch (preset) {
            case 'last30': return 'Ostatnie 30 dni';
            case 'thisMonth': return `${now.format('MMMM YYYY')} MTD`;
            case 'thisQuarter': return `Q${now.quarter()} ${now.year()}`;
            case 'ytd': return `${now.year()} YTD`;
            case 'custom': return 'Własny zakres';
            default: return preset;
        }
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Period selector — right-aligned */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                <Select
                    value={period}
                    onChange={(val) => setPeriod(val)}
                    style={{ width: 220 }}
                    options={[
                        { value: 'last30', label: getPeriodLabel('last30') },
                        { value: 'thisMonth', label: getPeriodLabel('thisMonth') },
                        { value: 'thisQuarter', label: getPeriodLabel('thisQuarter') },
                        { value: 'ytd', label: getPeriodLabel('ytd') },
                        { value: 'custom', label: 'Własny zakres' },
                    ]}
                />
                {period === 'custom' && (
                    <RangePicker
                        value={customRange}
                        onChange={(values) => setCustomRange(values as [dayjs.Dayjs, dayjs.Dayjs])}
                    />
                )}
            </div>

            {/* KPI Cards - equal height */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card style={{ height: '100%' }}>
                        <Statistic
                            title="Koszty (Actual)"
                            value={overview?.totalActual ?? 0}
                            precision={2}
                            suffix="PLN"
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card style={{ height: '100%' }}>
                        <Statistic
                            title="Budżet (Plan)"
                            value={overview?.totalPlanned ?? 0}
                            precision={2}
                            suffix="PLN"
                            prefix={<BarChartOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card style={{ height: '100%' }}>
                        <Statistic
                            title="Odchyłka"
                            value={Math.abs(variance)}
                            precision={2}
                            suffix={`PLN (${Math.abs(variancePercent).toFixed(1)}%)`}
                            prefix={isOverBudget ? <RiseOutlined /> : <FallOutlined />}
                            valueStyle={{ color: isOverBudget ? '#ff4d4f' : '#52c41a' }}
                        />
                        <span style={{ fontSize: 12, color: isOverBudget ? '#ff4d4f' : '#52c41a' }}>
                            {isOverBudget ? 'Przekroczenie' : 'Oszczędność'}
                        </span>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card style={{ height: '100%' }}>
                        <Statistic
                            title="Liczba kosztów"
                            value={overview?.costsCount ?? 0}
                            prefix={<FileTextOutlined />}
                        />
                        {(overview?.overdueTotal ?? 0) > 0 && (
                            <span
                                style={{ fontSize: 12, color: '#ff4d4f', cursor: 'pointer', textDecoration: 'underline dotted' }}
                                onClick={() => {
                                    setOverdueFilter(true);
                                    setActiveTab('actual');
                                }}
                                title="Kliknij, aby filtrować przeterminowane"
                            >
                                <WarningOutlined style={{ marginRight: 4 }} /> Przeterminowane: {Number(overview?.overdueTotal ?? 0).toFixed(2)} PLN
                            </span>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Top Categories */}
            {overview?.topCategories && overview.topCategories.length > 0 && (
                <Card size="small">
                    <Row gutter={16}>
                        <Col>
                            <strong>Top kategorie:</strong>
                        </Col>
                        {overview.topCategories.slice(0, 3).map((cat: any, idx: number) => (
                            <Col key={idx}>
                                <Badge
                                    color={['blue', 'green', 'orange'][idx]}
                                    text={`${COST_CATEGORIES.find(c => c.value === cat.category)?.label || cat.category}: ${Number(cat.amount).toFixed(2)} PLN`}
                                />
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            {/* Collapsible Charts Section - same pattern as Orders */}
            <Collapse
                activeKey={chartsExpanded ? ['charts'] : []}
                onChange={(keys) => {
                    const expanded = keys.includes('charts');
                    setChartsExpanded(expanded);
                    localStorage.setItem('finance-charts-expanded', String(expanded));
                }}
                items={[{
                    key: 'charts',
                    label: (
                        <span>
                            <LineChartOutlined style={{ marginRight: 8 }} />
                            Analiza kosztów
                        </span>
                    ),
                    children: (
                        <CostsCharts
                            overview={overview}
                            periodLabel={PERIOD_PRESETS.find(p => p.value === period)?.label || 'Niestandardowy okres'}
                            year={currentYear}
                        />
                    ),
                }]}
            />

            {/* Tabs: Actual vs Plan */}
            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        setActiveTab(key);
                        if (key !== 'actual') setOverdueFilter(false);
                    }}
                    items={[
                        {
                            key: 'actual',
                            label: <span><FileTextOutlined style={{ marginRight: 4 }} />Rejestr kosztów (Actual)</span>,
                            children: <CostsActualTab initialOverdueFilter={overdueFilter} />,
                        },
                        {
                            key: 'plan',
                            label: <span><BarChartOutlined style={{ marginRight: 4 }} />Budżet (Plan)</span>,
                            children: <CostsPlanTab year={currentYear} />,
                        },
                    ]}
                />
            </Card>
        </Space>
    );
}
